<script setup lang="ts">
  import { ref } from "vue";
  import {
    FlexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useVueTable,
    type Row,
    type ExpandedState,
    type VisibilityState,
  } from "@tanstack/vue-table";
  import { Loader2 } from "lucide-vue-next";
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
  import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
  import { Pagination } from "@/components/ui/pagination";
  import type { HistoTableRow } from "./types";
  import { columns } from "./columns";

  interface PaginationInfo {
    currentPage: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
  }

  const props = defineProps<{
    data: HistoTableRow[];
    loading?: boolean;
    columnVisibility: VisibilityState;
    pagination: PaginationInfo;
  }>();

  const emit = defineEmits<{
    rowClick: [row: HistoTableRow];
    pageChange: [page: number];
  }>();

  const expanded = ref<ExpandedState>({});

  const handlePageChange = (page: number) => {
    emit("pageChange", page);
  };

  const table = useVueTable({
    get data() {
      return props.data;
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.subRows,
    state: {
      get expanded() {
        return expanded.value;
      },
      get columnVisibility() {
        return props.columnVisibility;
      },
    },
    onExpandedChange: (updaterOrValue) => {
      expanded.value = typeof updaterOrValue === "function" ? updaterOrValue(expanded.value) : updaterOrValue;
    },
  });

  function getHeaderClass(columnId: string): string {
    const numericColumns = [
      "completesValidCapped",
      "revenue",
      "recruitmentMargin",
      "cpi",
      "clientCpi",
      "completionRate",
      "screenoutRate",
      "loi",
      "days",
      "cpm",
      "ctr",
      "cpc",
      "startRate",
      "cps",
      "similarity",
    ];
    if (numericColumns.includes(columnId)) {
      return "text-center";
    }
    return "";
  }

  function getCellClass(columnId: string, row: Row<HistoTableRow>): string {
    const classes: string[] = [];
    const numericColumns = [
      "completesValidCapped",
      "revenue",
      "recruitmentMargin",
      "cpi",
      "clientCpi",
      "completionRate",
      "screenoutRate",
      "loi",
      "days",
      "cpm",
      "ctr",
      "cpc",
      "startRate",
      "cps",
      "similarity",
    ];

    if (columnId === "expand") {
      classes.push("p-0");
    }

    if (numericColumns.includes(columnId)) {
      classes.push("text-center tabular-nums");
    }

    if (row.original.isSubRow) {
      classes.push("bg-muted/30");
    }

    return classes.join(" ");
  }

  function getRowClass(row: Row<HistoTableRow>): string {
    const classes = ["cursor-pointer", "hover:bg-muted/50", "transition-colors"];
    if (row.original.isSubRow) {
      classes.push("bg-muted/20");
    }
    return classes.join(" ");
  }

  function handleRowClick(row: Row<HistoTableRow>) {
    emit("rowClick", row.original);
  }
</script>

<template>
  <div class="flex flex-col [&_td]:py-2 [&_td]:text-xs [&_td]:font-medium [&_th]:text-xs relative">
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-background/80 flex items-center justify-center z-20">
      <div class="flex flex-col items-center gap-2">
        <Loader2 class="h-8 w-8 animate-spin text-primary" />
        <span class="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>

    <ScrollArea class="h-[calc(100vh-280px)] w-full flex-1">
      <div class="min-w-max">
        <Table class="bg-white">
          <TableHeader class="sticky top-0 z-10 bg-white">
            <TableRow
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id">
              <TableHead
                v-for="header in headerGroup.headers"
                :key="header.id"
                :class="getHeaderClass(header.column.id)"
                :style="{ width: `${header.getSize()}px` }">
                <template v-if="!header.isPlaceholder">
                  <FlexRender
                    :render="header.column.columnDef.header"
                    :props="header.getContext()" />
                </template>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <template v-if="table.getRowModel().rows?.length">
              <template
                v-for="row in table.getRowModel().rows"
                :key="row.id">
                <TableRow
                  :class="getRowClass(row)"
                  @click="handleRowClick(row)">
                  <TableCell
                    v-for="cell in row.getVisibleCells()"
                    :key="cell.id"
                    :class="getCellClass(cell.column.id, row)">
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()" />
                  </TableCell>
                </TableRow>
              </template>
            </template>
            <TableRow v-else-if="!loading">
              <TableCell
                :colspan="columns.length"
                class="h-24 text-center text-muted-foreground">
                No results.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>

    <Pagination
      :total="pagination.totalCount"
      :items-per-page="pagination.perPage"
      :default-page="pagination.currentPage"
      @update:page="handlePageChange" />
  </div>
</template>
